'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import {
  Sparkles,
  Bot,
  Globe,
  Share2,
  TrendingUp,
  CheckCircle2,
  Zap,
  Layers,
  Coins,
} from 'lucide-react';
import TTOSRobotMascot from './TTOSRobotMascot';

export const TTOSHeroV1: React.FC = () => {
  const [showRecModal, setShowRecModal] = useState(false);

  return (
    <section className="relative pt-32 pb-20 md:pt-36 md:pb-28 overflow-hidden bg-gradient-to-b from-[#FAF5FF]/70 via-white to-white">
      {/* Background Ambient Glows */}
      <div className="absolute top-12 left-1/2 -translate-x-1/2 w-[1000px] h-[550px] bg-gradient-to-tr from-purple-200/40 via-indigo-200/30 to-sky-200/40 blur-[130px] -z-10 rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Hero Column */}
          <div className="lg:col-span-6 space-y-6 text-left">
            
            {/* Top Pill Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-purple-50 border border-purple-200/70 text-purple-700 text-xs font-semibold shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-purple-600 animate-spin" style={{ animationDuration: '6s' }} />
              <span>India&apos;s Most Advanced AI Business OS</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-[54px] font-extrabold text-slate-900 tracking-tight leading-[1.12]">
              Your Entire Business.{' '}
              <br />
              <span className="bg-gradient-to-r from-purple-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent">
                Powered by AI.
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-lg text-slate-600 font-normal leading-relaxed max-w-xl">
              Website, SEO, Social Media, Content, Marketing, Automation and Business Tools — all inside one intelligent workspace.
            </p>

            {/* 4 Pill Grid Features */}
            <div className="grid grid-cols-2 gap-2.5 max-w-md pt-1">
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-purple-50/70 border border-purple-100 text-slate-700 text-xs font-medium">
                <span className="p-1 rounded-md bg-purple-600 text-white shrink-0">
                  <Sparkles className="w-3 h-3" />
                </span>
                <span>30+ AI Powered Tools</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-indigo-50/70 border border-indigo-100 text-slate-700 text-xs font-medium">
                <span className="p-1 rounded-md bg-indigo-600 text-white shrink-0">
                  <Bot className="w-3 h-3" />
                </span>
                <span>AI Business Manager</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-violet-50/70 border border-violet-100 text-slate-700 text-xs font-medium">
                <span className="p-1 rounded-md bg-violet-600 text-white shrink-0">
                  <Layers className="w-3 h-3" />
                </span>
                <span>Industry-Specific Templates</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-sky-50/70 border border-sky-100 text-slate-700 text-xs font-medium">
                <span className="p-1 rounded-md bg-sky-600 text-white shrink-0">
                  <Zap className="w-3 h-3" />
                </span>
                <span>End-to-End Automation</span>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap items-center gap-3.5 pt-2">
              <Link
                href="/register"
                className="px-7 py-3 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-bold shadow-lg shadow-purple-500/30 hover:shadow-purple-500/40 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              >
                Start Free
              </Link>
              <Link
                href="#how-it-works"
                className="px-6 py-3 rounded-full bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-slate-900 text-sm font-semibold transition-all duration-200 shadow-sm"
              >
                Explore TT OS
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center gap-6 pt-1 text-xs text-slate-500 font-medium">
              <div className="flex items-center gap-1.5 text-emerald-600">
                <CheckCircle2 className="w-4 h-4 fill-emerald-100" />
                <span className="text-slate-600">No Credit Card Required</span>
              </div>
              <div className="flex items-center gap-1.5 text-emerald-600">
                <CheckCircle2 className="w-4 h-4 fill-emerald-100" />
                <span className="text-slate-600">Set up in 2 Minutes</span>
              </div>
            </div>

          </div>

          {/* Right Hero Column: Interactive TT OS Dashboard Mockup */}
          <div className="lg:col-span-6 relative">
            
            {/* Violet/Blue Backdrop Glow */}
            <div className="absolute -inset-4 bg-gradient-to-tr from-purple-400/20 via-indigo-300/30 to-sky-300/20 rounded-3xl blur-2xl -z-10" />

            {/* Main Window Container */}
            <div className="bg-white rounded-2xl shadow-[0_20px_50px_rgba(79,70,229,0.12)] border border-slate-200/90 overflow-hidden flex flex-col md:flex-row transition-all duration-300 hover:shadow-[0_25px_60px_rgba(79,70,229,0.18)]">
              
              {/* Left Dark Sidebar */}
              <div className="w-full md:w-16 bg-[#0F172A] p-3 flex md:flex-col items-center justify-between md:justify-start gap-4 text-slate-400 border-r border-slate-800">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-600 to-cyan-400 flex items-center justify-center text-white font-black text-xs shadow-md">
                  TT
                </div>
                <div className="flex md:flex-col items-center gap-3 pt-2">
                  <button className="w-8 h-8 rounded-lg bg-indigo-600/30 text-indigo-400 flex items-center justify-center">
                    <Bot className="w-4 h-4" />
                  </button>
                  <button className="w-8 h-8 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center transition-colors">
                    <Globe className="w-4 h-4" />
                  </button>
                  <button className="w-8 h-8 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center transition-colors">
                    <Share2 className="w-4 h-4" />
                  </button>
                  <button className="w-8 h-8 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center transition-colors">
                    <TrendingUp className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-auto hidden md:block">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping" />
                </div>
              </div>

              {/* Main Dashboard Panel */}
              <div className="flex-1 p-5 md:p-6 space-y-4 bg-gradient-to-b from-slate-50/50 to-white">
                
                {/* Header Welcome Bar */}
                <div className="flex flex-wrap items-center justify-between gap-3 pb-2 border-b border-slate-100">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">
                      Happy Monday, Arjun! 🚀
                    </h3>
                    <p className="text-[11px] text-slate-500">
                      Your business is growing. Let&apos;s do more today.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200/80 text-amber-800 text-[11px] font-bold">
                      <Coins className="w-3 h-3 text-amber-500" />
                      <span>12,450</span>
                    </div>
                    <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-purple-100">
                      AS
                    </div>
                  </div>
                </div>

                {/* AI Business Manager Spotlight Card with Robot Mascot */}
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-purple-50/90 via-indigo-50/80 to-purple-100/70 border border-purple-200/70 p-4 flex items-center justify-between">
                  <div className="space-y-1.5 max-w-[240px] z-10">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-purple-600 animate-pulse" />
                      <span className="text-xs font-extrabold text-purple-900">
                        AI Business Manager
                      </span>
                    </div>
                    <p className="text-[11.5px] text-slate-700 leading-snug">
                      Your AI Manager has <span className="font-bold text-purple-700">6 recommendations</span> ready for this week.
                    </p>
                    <button
                      onClick={() => setShowRecModal(!showRecModal)}
                      className="mt-1 px-3 py-1 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-[10.5px] font-bold shadow-sm transition-colors"
                    >
                      {showRecModal ? 'Hide Action Plan' : 'View Recommendations'}
                    </button>
                  </div>

                  {/* 3D Robot Assistant */}
                  <div className="relative -my-3 -mr-2">
                    <TTOSRobotMascot size={110} glow={false} />
                  </div>
                </div>

                {/* Recommendations expandable modal/drawer */}
                {showRecModal && (
                  <div className="p-3 bg-white rounded-xl border border-purple-200 shadow-md space-y-1.5 text-xs animate-in fade-in">
                    <div className="flex items-center justify-between font-bold text-slate-800 pb-1 border-b border-slate-100">
                      <span>⚡ Priority Autonomous Actions:</span>
                      <span className="text-[10px] text-purple-600 font-semibold">Updated 5 min ago</span>
                    </div>
                    <div className="space-y-1 text-[11px] text-slate-600">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                        <span>Publish &quot;Top 5 Monsoon Specials&quot; carousel to Instagram &amp; Facebook</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                        <span>Send WhatsApp 15% re-engagement discount to 240 lapsed diners</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                        <span>Optimize Google Maps profile with 12 new high-volume local keywords</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* 4 Metric Cards Grid */}
                <div className="grid grid-cols-2 gap-2.5">
                  {/* Website */}
                  <div className="p-2.5 rounded-xl bg-white border border-slate-100 shadow-sm hover:border-indigo-200 transition-colors">
                    <div className="flex items-center justify-between text-[11px] text-slate-500">
                      <span>Website</span>
                      <span className="text-[10px] font-semibold text-emerald-600">Excellent</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="relative w-8 h-8 flex items-center justify-center">
                        <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                          <path
                            className="text-slate-100"
                            strokeWidth="3.5"
                            stroke="currentColor"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          <path
                            className="text-emerald-500"
                            strokeDasharray="95, 100"
                            strokeWidth="3.5"
                            strokeLinecap="round"
                            stroke="currentColor"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                        <span className="absolute text-[9px] font-bold text-slate-800">95%</span>
                      </div>
                      <span className="text-xs font-bold text-slate-900">Healthy</span>
                    </div>
                    <span className="text-[9.5px] text-indigo-600 font-medium hover:underline cursor-pointer">
                      View Details →
                    </span>
                  </div>

                  {/* SEO Score */}
                  <div className="p-2.5 rounded-xl bg-white border border-slate-100 shadow-sm hover:border-indigo-200 transition-colors">
                    <div className="flex items-center justify-between text-[11px] text-slate-500">
                      <span>SEO Score</span>
                      <span className="text-[10px] font-semibold text-amber-600">Good</span>
                    </div>
                    <div className="text-base font-extrabold text-slate-900 mt-1">
                      78<span className="text-xs text-slate-400">/100</span>
                    </div>
                    <span className="text-[9.5px] text-indigo-600 font-medium hover:underline cursor-pointer">
                      View Details →
                    </span>
                  </div>

                  {/* Social Reach */}
                  <div className="p-2.5 rounded-xl bg-white border border-slate-100 shadow-sm hover:border-indigo-200 transition-colors">
                    <div className="flex items-center justify-between text-[11px] text-slate-500">
                      <span>Social Reach</span>
                      <span className="text-[10px] font-bold text-emerald-600">+18.5%</span>
                    </div>
                    <div className="text-base font-extrabold text-slate-900 mt-1">
                      12.4K
                    </div>
                    <span className="text-[9.5px] text-indigo-600 font-medium hover:underline cursor-pointer">
                      View Details →
                    </span>
                  </div>

                  {/* Leads */}
                  <div className="p-2.5 rounded-xl bg-white border border-slate-100 shadow-sm hover:border-indigo-200 transition-colors">
                    <div className="flex items-center justify-between text-[11px] text-slate-500">
                      <span>Leads</span>
                      <span className="text-[10px] font-bold text-emerald-600">+32.4%</span>
                    </div>
                    <div className="text-base font-extrabold text-slate-900 mt-1">
                      1,245
                    </div>
                    <span className="text-[9.5px] text-indigo-600 font-medium hover:underline cursor-pointer">
                      View Details →
                    </span>
                  </div>
                </div>

                {/* Bottom Row: Recent Activity & Credits Bar */}
                <div className="pt-2 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  {/* Recent Activity */}
                  <div className="space-y-1">
                    <span className="text-[10.5px] font-bold text-slate-700 block">Recent Activity</span>
                    <div className="text-[10px] text-slate-500 space-y-1">
                      <div className="flex items-center justify-between">
                        <span>• AI Blog Generated</span>
                        <span className="text-slate-400">5 min ago</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>• Social Post Scheduled</span>
                        <span className="text-slate-400">15 min ago</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>• Website Page Updated</span>
                        <span className="text-slate-400">1 hour ago</span>
                      </div>
                    </div>
                  </div>

                  {/* Credits Used */}
                  <div className="space-y-1 bg-slate-50 p-2 rounded-lg border border-slate-100">
                    <div className="flex items-center justify-between text-[10.5px]">
                      <span className="font-bold text-slate-700">Credits Used Today</span>
                      <span className="text-[10px] text-purple-700 font-semibold">16%</span>
                    </div>
                    <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 h-full w-[16%]" />
                    </div>
                    <div className="flex items-center justify-between text-[9.5px] text-slate-500 pt-0.5">
                      <span>320 / 2,000</span>
                      <span className="text-indigo-600 font-medium hover:underline cursor-pointer">
                        Manage Credits
                      </span>
                    </div>
                  </div>
                </div>

              </div>

            </div>

          </div>

        </div>
      </div>
    </section>
  );
};

export default TTOSHeroV1;
